import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ChatbotWidget from './ChatbotWidget';
import LoginChatbotWidget from './LoginChatbotWidget';
import VoiceCallWidget from './VoiceCallWidget';
import {
  FAB_BOTTOM,
  FAB_EDGE,
  FAB_SIZE,
  PANEL_BOTTOM,
  PANEL_RIGHT,
  fabVerticalOffset,
} from './floatingAssistantsLayout';

const MENU_ACTIONS = [
  {
    id: 'password',
    title: 'Password Reset Assistant',
    emoji: '🔐',
    background: 'linear-gradient(135deg, #0ea5e9 0%, #3B5BDB 100%)',
    shadow: '0 6px 24px rgba(14,165,233,0.45)',
  },
  {
    id: 'voice',
    title: 'PowerDesk Voice Agent',
    emoji: '📞',
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    shadow: '0 6px 24px rgba(34,197,94,0.45)',
  },
  {
    id: 'ai',
    title: 'AI Assistant',
    emoji: '✦',
    background: 'linear-gradient(135deg, #3B5BDB 0%, #6366f1 100%)',
    shadow: '0 6px 24px rgba(59,91,219,0.45)',
  },
];

function ActionButton({ action, index, onClick }) {
  return (
    <motion.button
      type="button"
      key={action.id}
      initial={{ opacity: 0, y: 12, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.9 }}
      transition={{ duration: 0.18, delay: index * 0.04 }}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.94 }}
      title={action.title}
      onClick={() => onClick(action.id)}
      style={{
        position: 'fixed',
        bottom: fabVerticalOffset(index),
        right: FAB_EDGE,
        zIndex: 1001,
        width: FAB_SIZE,
        height: FAB_SIZE,
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        background: action.background,
        boxShadow: action.shadow,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 22,
        color: '#fff',
      }}
    >
      {action.emoji}
    </motion.button>
  );
}

export default function FloatingAssistantsHub({
  displayName = '',
  prefilledEmail = '',
  showAi = true,
  chatbotProps = {},
  voiceProps = {},
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);

  const actions = showAi ? MENU_ACTIONS : MENU_ACTIONS.filter((a) => a.id !== 'ai');
  const panelOpen = !!activePanel;

  const openPanel = (id) => {
    setMenuOpen(false);
    setActivePanel(id);
  };

  const closePanel = () => setActivePanel(null);

  const toggleHub = () => {
    if (panelOpen) {
      setActivePanel(null);
      return;
    }
    setMenuOpen((open) => !open);
  };

  const hub = (
    <>
      <AnimatePresence>
        {menuOpen && !panelOpen && actions.map((action, index) => (
          <ActionButton
            key={action.id}
            action={action}
            index={index}
            onClick={openPanel}
          />
        ))}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={toggleHub}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        title={panelOpen ? 'Close assistant' : 'Open PowerDesk assistants'}
        style={{
          position: 'fixed',
          bottom: FAB_BOTTOM,
          right: FAB_EDGE,
          zIndex: 1002,
          width: FAB_SIZE,
          height: FAB_SIZE,
          borderRadius: '50%',
          border: 'none',
          cursor: 'pointer',
          background: menuOpen || panelOpen
            ? '#EDF2F7'
            : 'linear-gradient(135deg, #3B5BDB 0%, #6366f1 100%)',
          boxShadow: menuOpen || panelOpen
            ? '0 2px 12px rgba(0,0,0,0.12)'
            : '0 6px 24px rgba(59,91,219,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s, box-shadow 0.2s',
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {menuOpen || panelOpen ? (
            <motion.svg
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.18 }}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1A202C"
              strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </motion.svg>
          ) : (
            <motion.span
              key="hub"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ fontSize: 24, color: '#FFFFFF', fontWeight: 700 }}
            >
              ✦
            </motion.span>
          )}
        </AnimatePresence>

        {!menuOpen && !panelOpen && (
          <motion.div
            animate={{ scale: [1, 1.6], opacity: [0.5, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: '#3B5BDB',
              zIndex: -1,
            }}
          />
        )}
      </motion.button>

      {showAi && (
        <ChatbotWidget
          displayName={displayName}
          prefilledEmail={prefilledEmail}
          chatbotProps={chatbotProps}
          showFab={false}
          open={activePanel === 'ai'}
          onOpenChange={(open) => (open ? setActivePanel('ai') : closePanel())}
          fabBottom={PANEL_BOTTOM}
          fabRight={PANEL_RIGHT}
        />
      )}

      <LoginChatbotWidget
        displayName={displayName}
        prefilledEmail={prefilledEmail}
        showFab={false}
        open={activePanel === 'password'}
        onOpenChange={(open) => (open ? setActivePanel('password') : closePanel())}
        fabBottom={PANEL_BOTTOM}
        fabRight={PANEL_RIGHT}
      />

      <VoiceCallWidget
        displayName={displayName}
        prefilledEmail={prefilledEmail}
        voiceProps={voiceProps}
        showFab={false}
        open={activePanel === 'voice'}
        onOpenChange={(open) => (open ? setActivePanel('voice') : closePanel())}
        fabBottom={PANEL_BOTTOM}
        fabRight={PANEL_RIGHT}
      />
    </>
  );

  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(hub, document.body);
}
